import { useState, useRef, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { convertFilterModelToQueryString } from '../utils/filterUtils';

/**
 * Generic hook for handling DataGrid search and filter with debouncing
 * Combines search (quickFilter) and advanced filter functionality
 */
export const useDataGridSearchAndFilter = ({ onDataFetch, debounceDelay = 500, resetPagination }) => {
    const [filterModel, setFilterModel] = useState({
        items: [],
        quickFilterValues: [],
    });

    const [searchText, setSearchText] = useState('');
    const debouncedHandlerRef = useRef();
    const onDataFetchRef = useRef(onDataFetch);

    // Update ref when onDataFetch changes
    useEffect(() => {
        onDataFetchRef.current = onDataFetch;
    }, [onDataFetch]);

    // Create debounced function that handles both search and filter
    // Only depends on debounceDelay, not on onDataFetch
    const debouncedFetchData = useMemo(
        () =>
            debounce((searchQuery, filterQuery) => {
                if (onDataFetchRef.current) {
                    onDataFetchRef.current({
                        search: searchQuery,
                        filter: filterQuery,
                        resetToFirstPage: true,
                    });
                }
            }, debounceDelay),
        [debounceDelay] // Only depend on debounceDelay, not onDataFetch
    );

    // Store ref to current debounced function for cleanup
    debouncedHandlerRef.current = debouncedFetchData;

    // Main filter change handler
    const onFilterChange = newFilterModel => {
        setFilterModel(newFilterModel);

        // Extract search text from quick filter
        const searchQuery = newFilterModel.quickFilterValues?.[0] || '';

        // Extract filter query from filter items
        const filterQuery = convertFilterModelToQueryString(newFilterModel);

        // Reset pagination when searching or filtering
        if (resetPagination) {
            resetPagination();
        }

        // Trigger debounced data fetch
        debouncedFetchData(searchQuery, filterQuery);
    };

    // Search change handler (for search input)
    const onSearchChange = event => {
        const { value } = event.target;
        setSearchText(value);

        // Update filter model with new search value
        const newFilterModel = {
            ...filterModel,
            quickFilterValues: value ? [value] : [],
        };
        onFilterChange(newFilterModel);
    };

    // Cancel search handler
    const onCancelSearch = () => {
        setSearchText('');

        // Update filter model to remove search
        const newFilterModel = {
            ...filterModel,
            quickFilterValues: [],
        };
        onFilterChange(newFilterModel);
    };

    // Get current search and filter values
    const getCurrentSearchAndFilter = () => {
        const searchQuery = filterModel.quickFilterValues?.[0] || '';
        const filterQuery = convertFilterModelToQueryString(filterModel);
        return { search: searchQuery, filter: filterQuery };
    };

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            if (debouncedHandlerRef.current) {
                debouncedHandlerRef.current.cancel();
            }
        };
    }, []);

    return {
        // State
        filterModel,
        searchText,

        // Handlers
        onFilterChange,
        onSearchChange,
        onCancelSearch,

        // Utilities
        getCurrentSearchAndFilter,

        // For DataGrid integration
        searchProps: {
            searchText,
            onSearchChange,
            onCancelSearch,
        },
        filterProps: {
            filterModel,
            onFilterModelChange: onFilterChange,
        },
    };
};
